/* Waukesha seal-kit configurator v1: exact, fail-closed, no remote writes. */
(function (root, factory) {
  var api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  if (root) root.WaukeshaSealKitConfiguratorV1 = api;
})(typeof globalThis !== 'undefined' ? globalThis : this, function () {
  'use strict';

  function normalizeToken(value) {
    return String(value == null ? '' : value).trim().toUpperCase();
  }

  function normalizeSku(value) {
    var normalized = normalizeToken(value);
    return normalized.endsWith('+') ? normalized.slice(0, -1) : normalized;
  }

  function deepFreeze(value) {
    if (!value || typeof value !== 'object' || Object.isFrozen(value)) return value;
    Object.freeze(value);
    Object.keys(value).forEach(function (key) { deepFreeze(value[key]); });
    return value;
  }

  function uniqueSorted(values) {
    return Array.from(new Set(values)).sort(function (a, b) {
      return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
    });
  }

  function Catalog(documentData) {
    if (!documentData || documentData.schemaVersion !== 1 || documentData.immutable !== true) {
      throw new Error('Unsupported or mutable Waukesha seal-kit catalog.');
    }
    this.data = deepFreeze(documentData);
    this.configurations = this.data.configurations;
    this.configurationById = deepFreeze(this.configurations.reduce(function (index, row) {
      index[row.id] = row;
      return index;
    }, Object.create(null)));
    this.kitBySku = deepFreeze(this.data.kits.reduce(function (index, kit) {
      index[kit.normalizedSku] = kit;
      return index;
    }, Object.create(null)));
    Object.freeze(this);
  }

  Catalog.prototype.series = function () {
    return uniqueSorted(this.configurations.map(function (row) { return row.series; }));
  };

  Catalog.prototype._matches = function (row, filters) {
    if (filters.series && normalizeToken(row.series) !== normalizeToken(filters.series)) return false;
    if (filters.model && !row.models.some(function (model) { return normalizeToken(model) === normalizeToken(filters.model); })) return false;
    if (filters.sealType && normalizeToken(row.sealType) !== normalizeToken(filters.sealType)) return false;
    if (filters.context) {
      var context = row.domain === 'universal' ? row.faceCombination : row.kitType;
      if (normalizeToken(context) !== normalizeToken(filters.context)) return false;
    }
    if (filters.elastomer && normalizeToken(row.elastomer) !== normalizeToken(filters.elastomer)) return false;
    return true;
  };

  Catalog.prototype.options = function (filters, field) {
    var rows = this.configurations.filter(function (row) { return this._matches(row, filters || {}); }, this);
    if (field === 'series') return uniqueSorted(rows.map(function (row) { return row.series; }));
    if (field === 'model') return uniqueSorted(rows.flatMap(function (row) { return row.models; }));
    if (field === 'sealType') return uniqueSorted(rows.map(function (row) { return row.sealType; }));
    if (field === 'context') return uniqueSorted(rows.map(function (row) { return row.domain === 'universal' ? row.faceCombination : row.kitType; }));
    if (field === 'elastomer') return uniqueSorted(rows.map(function (row) { return row.elastomer; }));
    throw new Error('Unknown option field: ' + field);
  };

  Catalog.prototype.resolve = function (input) {
    var required = ['series', 'model', 'sealType', 'context', 'elastomer'];
    var missing = required.filter(function (field) { return !normalizeToken(input && input[field]); });
    if (missing.length) return deepFreeze({ status: 'incomplete', missing: missing });

    var matches = this.configurations.filter(function (row) { return this._matches(row, input); }, this);
    if (!matches.length) return deepFreeze({ status: 'unsupported' });
    if (matches.length > 1) {
      return deepFreeze({ status: 'ambiguous', configurations: matches });
    }

    var configuration = matches[0];
    var kit = this.kitBySku[configuration.normalizedKitSku];
    if (!kit) throw new Error('Catalog integrity error: missing kit ' + configuration.normalizedKitSku);
    if (configuration.gate !== 'ready') {
      return deepFreeze({ status: 'gated', gate: configuration.gate, configuration: configuration, kit: kit });
    }
    return deepFreeze({ status: 'ready', configuration: configuration, kit: kit });
  };

  Catalog.prototype.findExactSku = function (sku) {
    var normalized = normalizeSku(sku);
    if (!normalized) return deepFreeze({ status: 'incomplete' });
    var kit = this.kitBySku[normalized];
    if (!kit) return deepFreeze({ status: 'not-found', normalizedSku: normalized });
    var applications = kit.configurationIds.map(function (id) { return this.configurationById[id]; }, this);
    return deepFreeze({
      status: 'found',
      kit: kit,
      applications: applications,
      gatedApplications: applications.filter(function (row) { return row.gate !== 'ready'; })
    });
  };

  Catalog.prototype.suggestSkus = function (value, limit) {
    var normalized = normalizeSku(value);
    if (!normalized) return [];
    var maximum = Math.max(1, Math.min(Number(limit) || 8, 20));
    return this.data.kits
      .filter(function (kit) { return kit.normalizedSku.indexOf(normalized) === 0; })
      .sort(function (a, b) { return a.normalizedSku.localeCompare(b.normalizedSku, undefined, { numeric: true, sensitivity: 'base' }); })
      .slice(0, maximum)
      .map(function (kit) {
        return deepFreeze({
          kit: kit,
          applications: kit.configurationIds.map(function (id) { return this.configurationById[id]; }, this)
        });
      }, this);
  };

  function element(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = text;
    return node;
  }

  function describeConfiguration(config) {
    var context = config.domain === 'universal'
      ? 'Face/sleeve: ' + config.faceCombination
      : 'Kit scope: ' + config.kitType;
    return [config.series, config.models.join(', '), config.sealType, context, config.elastomer].join(' · ');
  }

  function setStockStatus(cell, label, state) {
    cell.textContent = label;
    cell.className = 'tx-kit-bom__status tx-kit-stock tx-kit-stock--' + state;
  }

  function renderBom(container, kit, componentMapping, productRequests) {
    if (kit.intentionalAtomic) {
      container.appendChild(element('p', 'wskc-v1__notice wskc-v1__notice--atomic', kit.intentionalEmptyReason));
      return;
    }
    var wrapper = element('div', 'wskc-v1__table-wrap');
    var table = element('table', 'wskc-v1__bom');
    var head = element('thead');
    var headRow = element('tr');
    ['Item', 'Part number', 'Description', 'Qty', 'Availability'].forEach(function (label) { headRow.appendChild(element('th', '', label)); });
    head.appendChild(headRow);
    table.appendChild(head);
    var body = element('tbody');
    kit.bom.forEach(function (line) {
      var row = element('tr');
      row.appendChild(element('td', '', line.item == null ? '—' : String(line.item)));
      var partCell = element('td');
      var normalized = normalizeSku(line.sku);
      var record = componentMapping[normalized];
      var link = element('a', 'wskc-v1__part-link', line.sku);
      link.href = record ? record.productUrl : '/pages/contact?sku=' + encodeURIComponent(line.sku);
      partCell.appendChild(link);
      row.appendChild(partCell);
      row.appendChild(element('td', '', line.description));
      row.appendChild(element('td', '', String(line.qty) + (line.uom ? ' ' + line.uom : '')));
      var statusCell = element('td');
      row.appendChild(statusCell);
      if (!record) {
        link.href = '/search?type=product&q=' + encodeURIComponent(line.sku);
        setStockStatus(statusCell, 'Product search', 'contact');
      } else if (typeof record.availableQuantity === 'number') {
        setStockStatus(statusCell, record.availableQuantity + ' in stock', record.availableQuantity > 0 ? 'in' : 'out');
      } else {
        setStockStatus(statusCell, 'Checking…', 'checking');
        var jsonUrl = record.productUrl + '.js';
        if (!productRequests[jsonUrl]) {
          productRequests[jsonUrl] = fetch(jsonUrl, { credentials: 'same-origin' })
            .then(function (response) { if (!response.ok) throw new Error('product'); return response.json(); });
        }
        productRequests[jsonUrl].then(function (product) {
          var exact = (product.variants || []).filter(function (variant) { return normalizeSku(variant.sku) === normalized; });
          if (exact.length !== 1) {
            setStockStatus(statusCell, 'Contact us', 'contact');
          } else if (exact[0].available) {
            setStockStatus(statusCell, 'In stock', 'in');
          } else {
            setStockStatus(statusCell, '0 in stock', 'out');
          }
        }).catch(function () {
          setStockStatus(statusCell, 'Contact us', 'contact');
        });
      }
      body.appendChild(row);
    });
    table.appendChild(body);
    wrapper.appendChild(table);
    container.appendChild(wrapper);
  }

  function renderKitInventory(container, kit, kitRecord, productRequests) {
    var inventory = element('div', 'wskc-v1__kit-inventory');
    inventory.appendChild(element('strong', 'wskc-v1__kit-inventory-label', 'Triplex kit inventory'));
    var status = element('span');
    inventory.appendChild(status);
    container.appendChild(inventory);
    if (!kitRecord) {
      setStockStatus(status, 'Product page unavailable', 'contact');
      return;
    }
    if (typeof kitRecord.availableQuantity === 'number') {
      setStockStatus(status, kitRecord.availableQuantity + ' in stock', kitRecord.availableQuantity > 0 ? 'in' : 'out');
      return;
    }
    if (typeof kitRecord.shopifyAvailableQuantity === 'number') {
      setStockStatus(status, kitRecord.shopifyAvailableQuantity + ' in stock', kitRecord.shopifyAvailableQuantity > 0 ? 'in' : 'out');
      return;
    }
    setStockStatus(status, 'Checking…', 'checking');
    var jsonUrl = kitRecord.productUrl + '.js';
    if (!productRequests[jsonUrl]) {
      productRequests[jsonUrl] = fetch(jsonUrl, { credentials: 'same-origin' })
        .then(function (response) { if (!response.ok) throw new Error('product'); return response.json(); });
    }
    productRequests[jsonUrl].then(function (product) {
      var exact = (product.variants || []).filter(function (variant) { return normalizeSku(variant.sku) === kit.normalizedSku; });
      if (exact.length !== 1) setStockStatus(status, 'Contact us', 'contact');
      else setStockStatus(status, exact[0].available ? 'In stock' : '0 in stock', exact[0].available ? 'in' : 'out');
    }).catch(function () { setStockStatus(status, 'Contact us', 'contact'); });
  }

  function renderKit(container, kit, applications, heading, componentMapping, productRequests) {
    container.appendChild(element('p', 'wskc-v1__eyebrow', heading || 'Exact kit'));
    var kitRecord = componentMapping[kit.normalizedSku];
    var kitHeading = element('h3', 'wskc-v1__kit-sku');
    if (kitRecord) {
      var kitHeadingLink = element('a', 'wskc-v1__kit-title-link', kit.displaySku);
      kitHeadingLink.href = kitRecord.productUrl;
      kitHeading.appendChild(kitHeadingLink);
    } else {
      kitHeading.textContent = kit.displaySku;
    }
    container.appendChild(kitHeading);
    var searchLink = element('a', 'btn wskc-v1__shop-link', kitRecord ? 'View product page' : 'Search this exact kit');
    searchLink.href = kitRecord ? kitRecord.productUrl : '/search?type=product&q=' + encodeURIComponent(kit.displaySku);
    container.appendChild(searchLink);
    renderKitInventory(container, kit, kitRecord, productRequests);
    if (applications && applications.length) {
      container.appendChild(element('h4', 'wskc-v1__subhead', applications.length === 1 ? 'Verified configuration' : 'Verified applications'));
      var list = element('ul', 'wskc-v1__applications');
      applications.forEach(function (config) { list.appendChild(element('li', '', describeConfiguration(config))); });
      container.appendChild(list);
    }
    container.appendChild(element('h4', 'wskc-v1__subhead', 'OEM kit contents'));
    renderBom(container, kit, componentMapping, productRequests);
  }

  function setSelect(select, options, placeholder, preservedValue) {
    while (select.firstChild) select.removeChild(select.firstChild);
    var blank = element('option', '', placeholder);
    blank.value = '';
    select.appendChild(blank);
    options.forEach(function (value) {
      var option = element('option', '', value);
      option.value = value;
      select.appendChild(option);
    });
    if (options.some(function (value) { return value === preservedValue; })) select.value = preservedValue;
    select.disabled = options.length === 0;
  }

  function mount(host, catalog, componentMapping) {
    var form = host.querySelector('[data-wskc-form]');
    var result = host.querySelector('[data-wskc-result]');
    var reverseForm = host.querySelector('[data-wskc-reverse-form]');
    var reverseInput = host.querySelector('[data-wskc-sku]');
    var reverseResult = host.querySelector('[data-wskc-reverse-result]');
    var productRequests = {};
    var fields = {
      series: host.querySelector('[data-wskc-field="series"]'),
      model: host.querySelector('[data-wskc-field="model"]'),
      sealType: host.querySelector('[data-wskc-field="sealType"]'),
      context: host.querySelector('[data-wskc-field="context"]'),
      elastomer: host.querySelector('[data-wskc-field="elastomer"]')
    };
    var order = ['series', 'model', 'sealType', 'context', 'elastomer'];
    var suggestionBox = element('div', 'wskc-v1__suggestions');
    var suggestionItems = [];
    var activeSuggestion = -1;
    reverseInput.parentNode.classList.add('wskc-v1__lookup-field');
    suggestionBox.id = 'wskc-suggestions-' + Math.random().toString(36).slice(2);
    suggestionBox.setAttribute('role', 'listbox');
    suggestionBox.hidden = true;
    reverseInput.setAttribute('role', 'combobox');
    reverseInput.setAttribute('aria-autocomplete', 'list');
    reverseInput.setAttribute('aria-controls', suggestionBox.id);
    reverseInput.setAttribute('aria-expanded', 'false');
    reverseInput.parentNode.appendChild(suggestionBox);

    function closeSuggestions() {
      suggestionBox.hidden = true;
      reverseInput.setAttribute('aria-expanded', 'false');
      activeSuggestion = -1;
    }

    function chooseSuggestion(index) {
      var suggestion = suggestionItems[index];
      if (!suggestion) return;
      reverseInput.value = suggestion.kit.displaySku;
      closeSuggestions();
      if (typeof reverseForm.requestSubmit === 'function') reverseForm.requestSubmit();
      else reverseForm.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
    }

    function setActiveSuggestion(index) {
      var buttons = suggestionBox.querySelectorAll('button');
      if (!buttons.length) return;
      activeSuggestion = (index + buttons.length) % buttons.length;
      buttons.forEach(function (button, buttonIndex) {
        button.setAttribute('aria-selected', buttonIndex === activeSuggestion ? 'true' : 'false');
      });
      buttons[activeSuggestion].scrollIntoView({ block: 'nearest' });
    }

    function renderSuggestions() {
      while (suggestionBox.firstChild) suggestionBox.removeChild(suggestionBox.firstChild);
      var typed = normalizeSku(reverseInput.value);
      if (typed.length < 2) { suggestionItems = []; closeSuggestions(); return; }
      suggestionItems = catalog.suggestSkus(typed, 8).sort(function (a, b) {
        var aMapped = componentMapping[a.kit.normalizedSku] ? 0 : 1;
        var bMapped = componentMapping[b.kit.normalizedSku] ? 0 : 1;
        return aMapped - bMapped || a.kit.normalizedSku.localeCompare(b.kit.normalizedSku, undefined, { numeric: true });
      });
      if (!suggestionItems.length) { closeSuggestions(); return; }
      suggestionItems.forEach(function (suggestion, index) {
        var button = element('button', 'wskc-v1__suggestion');
        button.type = 'button';
        button.setAttribute('role', 'option');
        button.setAttribute('aria-selected', 'false');
        button.appendChild(element('strong', 'wskc-v1__suggestion-sku', suggestion.kit.displaySku));
        var application = suggestion.applications[0];
        var detail = application
          ? [application.series, application.models.join(', '), application.sealType, application.domain === 'universal' ? application.faceCombination : application.kitType, application.elastomer].join(' · ')
          : 'Canonical Waukesha seal kit';
        if (suggestion.applications.length > 1) detail += ' · +' + (suggestion.applications.length - 1) + ' application' + (suggestion.applications.length > 2 ? 's' : '');
        button.appendChild(element('span', 'wskc-v1__suggestion-detail', detail));
        var record = componentMapping[suggestion.kit.normalizedSku];
        var availability = 'Canonical option';
        if (record && typeof record.availableQuantity === 'number') availability = record.availableQuantity + ' in stock';
        else if (record && typeof record.shopifyAvailableQuantity === 'number') availability = record.shopifyAvailableQuantity + ' in stock';
        else if (record) availability = 'Product page available';
        button.appendChild(element('span', 'wskc-v1__suggestion-stock', availability));
        button.addEventListener('click', function () { chooseSuggestion(index); });
        suggestionBox.appendChild(button);
      });
      suggestionBox.hidden = false;
      reverseInput.setAttribute('aria-expanded', 'true');
      activeSuggestion = -1;
    }

    reverseInput.addEventListener('input', renderSuggestions);
    reverseInput.addEventListener('keydown', function (event) {
      if (suggestionBox.hidden) return;
      if (event.key === 'ArrowDown') { event.preventDefault(); setActiveSuggestion(activeSuggestion + 1); }
      else if (event.key === 'ArrowUp') { event.preventDefault(); setActiveSuggestion(activeSuggestion - 1); }
      else if (event.key === 'Enter' && activeSuggestion >= 0) { event.preventDefault(); chooseSuggestion(activeSuggestion); }
      else if (event.key === 'Escape') { event.preventDefault(); closeSuggestions(); }
    });
    document.addEventListener('click', function (event) {
      if (!reverseInput.parentNode.contains(event.target)) closeSuggestions();
    });

    function values(beforeIndex) {
      var output = {};
      order.slice(0, beforeIndex == null ? order.length : beforeIndex).forEach(function (name) { output[name] = fields[name].value; });
      return output;
    }

    function refresh(startIndex) {
      order.slice(startIndex).forEach(function (name, relativeIndex) {
        var index = startIndex + relativeIndex;
        var previous = fields[name].value;
        var labels = { series: 'Choose family / series', model: 'Choose exact model', sealType: 'Choose seal type', context: 'Choose face, sleeve, or kit scope', elastomer: 'Choose elastomer' };
        setSelect(fields[name], catalog.options(values(index), name), labels[name], previous);
      });
      var selectedSeries = fields.series.value;
      var isUniversal = /^U[123]$/i.test(selectedSeries);
      var contextLabel = host.querySelector('[data-wskc-context-label]');
      contextLabel.textContent = isUniversal ? 'Face / sleeve combination' : 'Kit scope / type';
    }

    order.forEach(function (name, index) {
      fields[name].addEventListener('change', function () {
        order.slice(index + 1).forEach(function (later) { fields[later].value = ''; });
        refresh(index + 1);
        result.hidden = true;
      });
    });

    form.addEventListener('submit', function (event) {
      event.preventDefault();
      while (result.firstChild) result.removeChild(result.firstChild);
      result.hidden = false;
      var resolution = catalog.resolve(values());
      if (resolution.status === 'ready') {
        renderKit(result, resolution.kit, [resolution.configuration], 'Exact canonical match', componentMapping, productRequests);
      } else if (resolution.status === 'ambiguous') {
        result.appendChild(element('h3', '', 'Manual review required'));
        result.appendChild(element('p', 'wskc-v1__notice wskc-v1__notice--warning', 'This complete selector key maps to more than one source-backed OEM kit. No kit has been selected. Confirm the serial number and current SPX configuration.'));
        var list = element('ul', 'wskc-v1__applications');
        resolution.configurations.forEach(function (config) { list.appendChild(element('li', '', config.kitSku + ' — ' + describeConfiguration(config))); });
        result.appendChild(list);
      } else if (resolution.status === 'gated') {
        result.appendChild(element('h3', '', 'Serial number required'));
        result.appendChild(element('p', 'wskc-v1__notice wskc-v1__notice--warning', resolution.configuration.gateMessage || 'This configuration is serial-gated. Confirm the pump serial number with SPX FLOW Application Engineering before selecting a kit.'));
        result.appendChild(element('p', '', 'Candidate source record: ' + resolution.kit.displaySku + '. This is not an order-ready resolution.'));
      } else if (resolution.status === 'incomplete') {
        result.appendChild(element('p', 'wskc-v1__notice wskc-v1__notice--warning', 'Complete every selector field.'));
      } else {
        result.appendChild(element('p', 'wskc-v1__notice wskc-v1__notice--warning', 'Unsupported exact configuration. No kit has been inferred. Confirm the nameplate, serial number, and current SPX data.'));
      }
    });

    reverseForm.addEventListener('submit', function (event) {
      event.preventDefault();
      closeSuggestions();
      while (reverseResult.firstChild) reverseResult.removeChild(reverseResult.firstChild);
      reverseResult.hidden = false;
      var lookup = catalog.findExactSku(reverseInput.value);
      if (lookup.status === 'found') {
        if (lookup.gatedApplications.length) {
          reverseResult.appendChild(element('h3', '', 'Serial number required'));
          reverseResult.appendChild(element('p', 'wskc-v1__notice wskc-v1__notice--warning', lookup.gatedApplications[0].gateMessage || 'This kit has a serial-gated application. Confirm the pump serial number before ordering.'));
          reverseResult.appendChild(element('p', '', 'Exact source SKU: ' + lookup.kit.displaySku + '. This is not an order-ready resolution.'));
        } else {
          renderKit(reverseResult, lookup.kit, lookup.applications, 'Exact SKU match', componentMapping, productRequests);
        }
      } else if (lookup.status === 'not-found') {
        reverseResult.appendChild(element('p', 'wskc-v1__notice wskc-v1__notice--warning', 'No exact canonical kit SKU match. Similar-looking part numbers are not substituted.'));
      } else {
        reverseResult.appendChild(element('p', 'wskc-v1__notice wskc-v1__notice--warning', 'Enter an exact OEM kit SKU.'));
      }
    });

    refresh(0);
    host.setAttribute('data-wskc-ready', 'true');
  }

  function boot() {
    if (typeof document === 'undefined') return;
    document.querySelectorAll('[data-wskc-v1]').forEach(function (host) {
      var url = host.getAttribute('data-catalog-url');
      var componentMapUrl = host.getAttribute('data-component-map-url');
      var status = host.querySelector('[data-wskc-loading]');
      var catalogRequest = fetch(url, { credentials: 'same-origin' })
        .then(function (response) { if (!response.ok) throw new Error('HTTP ' + response.status); return response.json(); });
      var mappingRequest = fetch(componentMapUrl, { credentials: 'same-origin' })
        .then(function (response) { if (!response.ok) throw new Error('HTTP ' + response.status); return response.json(); })
        .then(function (payload) { return payload.mapping || {}; })
        .catch(function () { return {}; });
      Promise.all([catalogRequest, mappingRequest])
        .then(function (payloads) {
          mount(host, new Catalog(payloads[0]), payloads[1]);
          if (status) status.hidden = true;
        })
        .catch(function (error) {
          if (status) {
            status.textContent = 'The canonical seal-kit catalog is unavailable. No kit can be resolved right now.';
            status.className += ' wskc-v1__notice--warning';
          }
          if (typeof console !== 'undefined') console.error('Waukesha configurator:', error);
        });
    });
  }

  if (typeof document !== 'undefined') {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
    else boot();
  }

  return { Catalog: Catalog, deepFreeze: deepFreeze, normalizeSku: normalizeSku, normalizeToken: normalizeToken, mount: mount };
});
